import { useContext, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { EnvContext } from "@/contexts/appContexts";
import { ApiError, isApiError } from "@/services/bedrock/apiError";
import { BackgroundAssetServices } from "@/services/bedrock/BackgroundAssetServices";
import { DesignTemplateServices } from "@/services/bedrock/DesignTemplateServices";
import { BackgroundRatio } from "@/services/bedrock/dtos/BackgroundAsset";
import {
  ActorRef,
  DesignPackageDetail,
  DesignTemplateSummary,
  PreviewSet,
} from "@/services/bedrock/dtos/DesignPackage";
import { designKeys } from "@/services/bedrock/queryKeys";
import { getDownloadURLFirebase, saveElementToFirebaseCancellable } from "@/utils/firebase";
import { slotRatioFor } from "../slideState";
import { inFlightMessage } from "./useDesignPackageMutations";
import { forgetPreviewRenders } from "./usePreviews";

const GENERIC_ERROR = "Something went wrong. Try again.";
export const UPLOAD_TOO_LARGE = "Choose a JPG or PNG under 10 MB.";
export const UPLOAD_FAILED = "That photo couldn't be uploaded. Try again.";
export const UPLOAD_TOO_SMALL = "That photo is too small. Use one at least 600 px on each side.";
export const ATTACH_FAILED = "That background couldn't be added. Try again.";
export const GENERATE_FAILED = "We couldn't create new backgrounds. Try again.";
export const REMOVE_DEFAULT_TIP = "Pick a different default first";

/** What the upload copy promises ("a JPG or PNG"); other formats may not render. */
export const UPLOAD_TYPES = ["image/jpeg", "image/png"];
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export interface BackgroundVariables {
  templateId: number;
  assetId: number;
}

/** Thrown after the hook has already told the coach what went wrong. */
class ReportedError extends Error {}

function inFlightToast(error: ApiError) {
  const name = (error.details as { actor?: ActorRef | null } | undefined)?.actor?.name;
  toast.error(name ? inFlightMessage(name) : "Wait for the current change to finish.");
}

// Same file picked again: name, size, and modification time.
const fileKey = (file: File) => `${file.name}:${file.size}:${file.lastModified}`;

/** An upload rejected for being under the minimum size. */
export function isTooSmall(error: ApiError): boolean {
  const url = (error.details as { fields?: { url?: unknown } } | undefined)?.fields?.url;
  return Array.isArray(url) && url.includes("too_small");
}

/**
 * Background mutations for one package. Template responses replace the owning item's
 * `activeTemplate` in the cached detail, so the tray updates without a refetch.
 */
export function useBackgroundMutations(packageId: number) {
  const queryClient = useQueryClient();
  const envValues = useContext(EnvContext);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  // Photos already in the library whose attach failed; picking the same file again only retries the attach.
  const unattachedUploads = useRef(new Map<string, number>());
  const detailKey = designKeys.detail(packageId);
  const previewsPrefix = designKeys.previews(packageId, null).slice(0, 2);
  const requestsPrefix = designKeys.backgrounds.requests({}).slice(0, 1);

  const invalidate = (...keys: (readonly unknown[])[]) =>
    keys.forEach(queryKey => queryClient.invalidateQueries({ queryKey }));

  const writeTemplate = (template: DesignTemplateSummary) => {
    queryClient.setQueryData<DesignPackageDetail>(detailKey, pkg =>
      pkg
        ? {
            ...pkg,
            items: pkg.items.map(item =>
              item.activeTemplate.id === template.id || item.id === template.designPackageItemId
                ? { ...item, activeTemplate: template }
                : item
            ),
          }
        : pkg
    );
    invalidate(designKeys.activity(packageId), designKeys.all);
  };

  // The ratio the slide's background slot asks for, so new images arrive in the shape the tray shows.
  const slotRatio = (templateId: number): BackgroundRatio | undefined => {
    const pkg = queryClient.getQueryData<DesignPackageDetail>(detailKey);
    const item = pkg?.items.find(i => i.activeTemplate.id === templateId);
    return pkg && item ? slotRatioFor(item, pkg) : undefined;
  };

  // Marking the slide stale shows the overlay right away and lets usePreviews re-render it. Refetching
  // previews here instead would briefly bring back the old image before the new render starts.
  const markSlideStale = (templateId: number) => {
    const detail = queryClient.getQueryData<DesignPackageDetail>(detailKey);
    const itemId = detail?.items.find(i => i.activeTemplate.id === templateId)?.id;
    if (itemId == null) return;
    queryClient.setQueriesData<PreviewSet[]>({ queryKey: previewsPrefix }, sets =>
      sets?.map(set => ({
        ...set,
        slides: set.slides.map(slide =>
          slide.designPackageItemId === itemId ? { ...slide, status: "stale" as const } : slide
        ),
      }))
    );
  };

  const generationError = (error: unknown) => {
    if (isApiError(error, "refine_in_progress")) inFlightToast(error);
    else toast.error(GENERATE_FAILED);
  };

  const attachBackground = useMutation({
    mutationFn: ({ templateId, assetId }: BackgroundVariables) =>
      DesignTemplateServices.attachBackground(templateId, assetId),
    onSuccess: writeTemplate,
    onError: () => toast.error(ATTACH_FAILED),
  });

  const removeBackground = useMutation({
    mutationFn: ({ templateId, assetId }: BackgroundVariables) =>
      DesignTemplateServices.removeBackground(templateId, assetId),
    onSuccess: writeTemplate,
    onError: error => toast.error(isApiError(error, "validation_error") ? REMOVE_DEFAULT_TIP : GENERIC_ERROR),
  });

  const setDefault = useMutation({
    mutationFn: ({ templateId, assetId }: BackgroundVariables) =>
      DesignTemplateServices.setDefaultBackground(templateId, assetId),
    onSuccess: template => {
      writeTemplate(template);
      forgetPreviewRenders(packageId);
      markSlideStale(template.id);
      toast.success("Default background updated");
    },
    onError: error => {
      if (isApiError(error, "refine_in_progress")) inFlightToast(error);
      else toast.error(GENERIC_ERROR);
    },
  });

  const attachUploaded = async (templateId: number, file: File, assetId: number) => {
    try {
      const template = await DesignTemplateServices.attachBackground(templateId, assetId);
      unattachedUploads.current.delete(fileKey(file));
      return { template, assetId };
    } catch {
      unattachedUploads.current.set(fileKey(file), assetId);
      toast.error(ATTACH_FAILED);
      throw new ReportedError();
    }
  };

  /** Firebase, then a reusable library asset, then attached to the slide; resolves to the new asset's id. */
  const uploadBackground = useMutation({
    mutationFn: async ({ templateId, file }: { templateId: number; file: File }) => {
      if (!UPLOAD_TYPES.includes(file.type) || file.size > MAX_UPLOAD_BYTES) {
        toast.error(UPLOAD_TOO_LARGE);
        throw new ReportedError();
      }

      const pendingAssetId = unattachedUploads.current.get(fileKey(file));
      if (pendingAssetId != null) return attachUploaded(templateId, file, pendingAssetId);

      const node = `design-backgrounds/${packageId}`;
      // The Firebase helper appends the extension from the file type.
      const filename = uuidv4();
      setUploadProgress(0);
      let url: string;
      try {
        const task = saveElementToFirebaseCancellable(envValues, node, file, filename);
        task.on("state_changed", snapshot => {
          if (snapshot.totalBytes > 0)
            setUploadProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
        });
        await task;
        url = await getDownloadURLFirebase(envValues, node, file, filename);
      } catch {
        toast.error(UPLOAD_FAILED);
        throw new ReportedError();
      }

      let assetId: number;
      try {
        assetId = (await BackgroundAssetServices.upload({ url })).id;
      } catch (error) {
        if (isApiError(error, "validation_error")) toast.error(isTooSmall(error) ? UPLOAD_TOO_SMALL : UPLOAD_FAILED);
        else toast.error(GENERIC_ERROR);
        throw new ReportedError();
      }
      invalidate(designKeys.backgrounds.all);
      return attachUploaded(templateId, file, assetId);
    },
    onSuccess: ({ template }) => writeTemplate(template),
    onSettled: () => setUploadProgress(null),
  });

  const generateBackgrounds = useMutation({
    mutationFn: ({ templateId, hint, count }: { templateId: number; hint?: string; count: 1 | 2 | 3 }) => {
      const ratio = slotRatio(templateId);
      if (!ratio) throw new Error(`Template ${templateId} is not on this design`);
      return BackgroundAssetServices.generate({
        ...(hint ? { hint } : {}),
        count,
        ratio,
        designTemplateId: templateId,
      });
    },
    onSuccess: () => invalidate(requestsPrefix, designKeys.activity(packageId)),
    onError: generationError,
  });

  const refineBackground = useMutation({
    mutationFn: ({ templateId, assetId, instruction }: BackgroundVariables & { instruction: string }) =>
      BackgroundAssetServices.refine(assetId, { instruction, designTemplateId: templateId }),
    onSuccess: () => invalidate(requestsPrefix, designKeys.activity(packageId)),
    onError: generationError,
  });

  return {
    attachBackground,
    removeBackground,
    setDefault,
    uploadBackground,
    uploadProgress,
    generateBackgrounds,
    refineBackground,
  };
}
